import React from 'react';
import { X, Clock, ArrowUpRight, Trash2, Calendar, User } from 'lucide-react';
import { SavedInvoice } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  history: SavedInvoice[];
  onLoad: (invoice: SavedInvoice) => void;
  onClear: () => void;
}

const HistorySidebar: React.FC<Props> = ({ isOpen, onClose, history, onLoad, onClear }) => {
  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-[100] transition-opacity duration-500 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-slate-950 z-[110] shadow-2xl transition-transform duration-700 ease-in-out border-l border-slate-200 dark:border-slate-800 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-500 rounded-xl text-white">
              <Clock size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">
                Vault
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                Local History
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-4">
          {history.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-center opacity-40">
              <Clock size={48} className="mb-4 text-slate-300" />
              <p className="text-sm font-bold text-slate-500">No archived documents found.</p>
            </div>
          ) : (
            history.map((invoice) => (
              <div
                key={invoice.id + invoice.savedAt}
                className="group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-3xl hover:border-brand-500 transition-all cursor-pointer shadow-sm hover:shadow-xl hover:shadow-brand-500/5"
                onClick={() => {
                  onLoad(invoice);
                  onClose();
                }}
              >
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-black bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full uppercase text-slate-500">
                    {invoice.number || 'Draft'}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400">
                    {new Date(invoice.savedAt).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="text-lg font-black text-slate-900 dark:text-white mb-1 line-clamp-1">
                  {invoice.recipientCompany || invoice.recipientName || 'Untitled Project'}
                </h3>
                <div className="flex items-center gap-4 text-xs font-bold text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={12} /> {invoice.date}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User size={12} /> {invoice.senderName?.split(' ')[0]}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-50 dark:border-slate-800 flex justify-end">
                  <div className="text-brand-500 group-hover:translate-x-1 transition-transform">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {history.length > 0 && (
          <div className="p-8 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => {
                if (confirm('Clear all local history?')) onClear();
              }}
              className="w-full py-4 bg-slate-50 dark:bg-slate-900 text-red-500 rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all"
            >
              <Trash2 size={16} /> Purge Vault
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default HistorySidebar;
